import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Exact,
  GetAllSchoolsQuery,
  Maybe,
  SchoolWhereInput,
  useGetAllSchoolsLazyQuery,
  useGetSchoolLazyQuery,
} from "generated/graphql";
import { QueryLazyOptions, useReactiveVar } from "@apollo/client";
import { authenticatedUserVar } from "store/authenticatedUser";

export type CurrentSchool = {
  id: string;
  name: string;
} | null;

interface ISchoolsContext {
  currentSchool: CurrentSchool;
  setCurrentSchool: React.Dispatch<React.SetStateAction<CurrentSchool>>;
  allSchools: GetAllSchoolsQuery | undefined;
  fetchSchools: (
    options?:
      | QueryLazyOptions<
          Exact<{
            where?: Maybe<SchoolWhereInput> | undefined;
          }>
        >
      | undefined
  ) => void;
  fetchInitialSchool: (
    options?:
      | QueryLazyOptions<
          Exact<{
            id: string;
          }>
        >
      | undefined
  ) => void;
}

const SchoolsContext = createContext<ISchoolsContext>({} as ISchoolsContext);

export const SchoolsProvider = ({ children }: { children: JSX.Element }) => {
  const authenticatedUser = useReactiveVar(authenticatedUserVar);
  const firstSchoolSet = useRef(false);
  const [currentSchool, setCurrentSchool] = useState<CurrentSchool>(null);
  const [allSchools, setAllSchools] = useState<GetAllSchoolsQuery>();

  const [
    fetchSchools,
    { data: allSchoolsData, loading: allSchoolsDataLoading },
  ] = useGetAllSchoolsLazyQuery();
  const [
    fetchInitialSchool,
    { data: initialSchoolData, loading: initialSchoolDataLoading },
  ] = useGetSchoolLazyQuery();

  useEffect(() => {
    if (!authenticatedUser) {
      setCurrentSchool(null);
      setAllSchools(undefined);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    if (currentSchool) {
      localStorage.setItem("CF_currentSchool", currentSchool?.id);
    }
  }, [currentSchool]);

  useEffect(() => {
    setAllSchools(allSchoolsData);
  }, [allSchoolsData]);

  useEffect(() => {}, [allSchoolsData]);

  useEffect(() => {
    if (!firstSchoolSet.current) {
      if (!initialSchoolDataLoading && !allSchoolsDataLoading) {
        if (initialSchoolData?.school?.id) {
          console.log("set SChool");
          setCurrentSchool({
            id: initialSchoolData.school.id,
            name: initialSchoolData.school.name || "",
          });
          firstSchoolSet.current = true;
        } else {
          const firstLoadedSchool = allSchoolsData?.schools?.[0];
          if (firstLoadedSchool) {
            console.log("set SChool");
            setCurrentSchool({
              id: firstLoadedSchool.id,
              name: firstLoadedSchool.name || "",
            });
            firstSchoolSet.current = true;
          }
        }
      }
    }
  }, [
    initialSchoolData,
    allSchoolsData,
    initialSchoolDataLoading,
    allSchoolsDataLoading,
  ]);

  return (
    <SchoolsContext.Provider
      value={{
        currentSchool,
        setCurrentSchool,
        allSchools,
        fetchSchools,
        fetchInitialSchool,
      }}
    >
      {children}
    </SchoolsContext.Provider>
  );
};

export const useSchools = () => useContext(SchoolsContext);
